import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Button, Card, EmptyState, ErrorState, Page, PageHeader, Skeleton } from '@indipia/ui-core';
import { billingApi } from '../api/billingApi';
import type { StaffFeeTier } from '../api/billingApi';
import { TIER_COPY } from '../constants/tierCopy';

function formatWon(n: number | null | undefined) {
  if (n == null) return '—';
  return n.toLocaleString('ko-KR');
}

function formatDate(iso: string | null | undefined) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit' });
}

/** 구간표(C-5-5)는 시행 예정이라도 항상 보여주지만, 슬라이더 계산은 실제로 지금 적용 중인
 *  구간만 써야 한다(사용자 요청, 2026-08-31) — validFromDtm이 미래면 아직 미적용. */
function isEffectiveNow(tier: StaffFeeTier): boolean {
  return !tier.validFromDtm || new Date(tier.validFromDtm).getTime() <= Date.now();
}

/** BE StaffFeeTierRepository.findApplicableTier와 동일 로직 — 슬라이더·구간표 둘 다 이 하나의
 *  API 응답을 재사용한다(기능명세서 C-5-5 비고, 별도 호출 불필요). */
function findTier(tiers: StaffFeeTier[], count: number): StaffFeeTier | undefined {
  return tiers.find((t) => (t.minCnt == null || count >= t.minCnt) && (t.maxCnt == null || count <= t.maxCnt));
}

/** isHighest=true면 maxCnt 값과 무관하게 "이상"으로 표시한다 — 실제 시드 데이터가 "무제한"을
 *  NULL이 아니라 999 같은 큰 정수 sentinel로 넣어두는 경우가 있어서(2026-08-31 확인), 최상위
 *  구간(정렬 후 마지막 tier)인지로 판단한다. 계산(findTier)에는 영향 없음 — 표시 라벨만 보정. */
function tierRangeLabel(tier: StaffFeeTier, isHighest: boolean): string {
  if (isHighest) return `${tier.minCnt}명 이상`;
  if (tier.minCnt == null) return `~${tier.maxCnt}명`;
  return `${tier.minCnt}~${tier.maxCnt}명`;
}

const SLIDER_MAX = 7;

export function StaffFeePage() {
  const navigate = useNavigate();
  const [sliderCount, setSliderCount] = useState(0);
  const [sliderTouched, setSliderTouched] = useState(false);

  const subscriptionQuery = useQuery({
    queryKey: ['billing', 'subscription'],
    queryFn: async ({ signal }) => (await billingApi.getSubscription(signal)).data.data,
  });
  const staffUsageQuery = useQuery({
    queryKey: ['billing', 'staffUsage'],
    queryFn: async ({ signal }) => (await billingApi.getStaffUsage(signal)).data.data,
  });
  const tiersQuery = useQuery({
    queryKey: ['billing', 'staffFeeTiers'],
    queryFn: async ({ signal }) => (await billingApi.getStaffFeeTiers(signal)).data.data,
  });

  // 슬라이더 초기값은 현재 실제 소속 인원 — 로드 직후엔 지금 상태 그대로 보여주고, 사용자가
  // 직접 움직이기 시작하면(sliderTouched) 더는 실제값으로 되돌리지 않는다.
  useEffect(() => {
    if (!sliderTouched && staffUsageQuery.data) {
      setSliderCount(Math.min(staffUsageQuery.data.staffCount, SLIDER_MAX));
    }
  }, [sliderTouched, staffUsageQuery.data]);

  const tiers = tiersQuery.data ?? [];
  // 구간표(displayTiers)는 tiers를 그대로 쓰고(시행 예정 포함), 실제 계산(슬라이더·예상 청구 금액)엔
  // 지금 적용 중인 구간만 골라 쓴다.
  const activeTiers = tiers.filter(isEffectiveNow);
  const policyEffectiveFrom = tiers.find((t) => t.validFromDtm)?.validFromDtm ?? null;
  const currentTier = findTier(activeTiers, sliderCount);
  const sliderSurcharge = currentTier?.surchargeAmount ?? 0;

  const sub = subscriptionQuery.data;
  // 예상 청구 금액은 요금제 선택 없이 지금 실제 구독 중인 티어 기준으로만 보여준다(사용자 요청,
  // 2026-08-31 — 비교 버튼 제거).
  const baseMonthly = sub?.monthlyPrice ?? 0;
  // "다음 결제 예정 금액"은 실제 청구액을 나타내는 카드라 슬라이더(SLIDER_MAX=7로 상한이 있는
  // 시뮬레이션용 값)가 아니라 서버가 계산한 실제 인원 기준 추가요금(getStaffUsage)을 그대로 써야
  // 한다 — 안 그러면 직원이 7명을 넘는 스튜디오는 실제보다 적은 금액이 "다음 결제 예정 금액"으로
  // 뜬다(2026-09-11 리뷰에서 발견·수정). sliderSurcharge는 위 시뮬레이션 박스 전용으로만 남긴다.
  const realStaffCount = staffUsageQuery.data?.staffCount ?? 0;
  const realSurcharge = staffUsageQuery.data?.surchargeAmount ?? 0;
  // 직원 추가요금 정책은 유료 티어 전용(C-5-7) — FREE 구독자는 추가요금을 더하지 않는다.
  const applicableSurcharge = sub?.tierCode === 'FREE' ? 0 : realSurcharge;
  const estimatedTotal = baseMonthly + applicableSurcharge;

  // 0~(첫 구간 시작 -1)명 구간은 실제 row가 없어(추가요금 0) FE에서 합성해 보여준다(C-5-5 비고)
  const displayTiers = useMemo(() => {
    if (tiers.length === 0) return [];
    const first = tiers[0];
    const freeUpperBound = (first.minCnt ?? 1) - 1;
    const rows: { label: string; amount: number }[] = [];
    if (freeUpperBound >= 0) rows.push({ label: `0~${freeUpperBound}명`, amount: 0 });
    tiers.forEach((t, i) =>
      rows.push({ label: tierRangeLabel(t, i === tiers.length - 1), amount: t.surchargeAmount }),
    );
    return rows;
  }, [tiers]);

  return (
    <Page width="lg">
      <PageHeader title="직원 계정 추가요금 안내" onBack={() => navigate('/agents/billing/manage')} />

      {/* 인원수 시뮬레이션 슬라이더 (C-5-3) */}
      <div className="mt-06 rounded-xl bg-group p-06 text-group-onPrimary">
        {tiersQuery.isLoading ? (
          <Skeleton height={160} />
        ) : tiersQuery.isError ? (
          <ErrorState title="요금 정책을 불러오지 못했습니다" />
        ) : (
          <>
            <div className="flex items-center justify-between text-caption opacity-80">
              <span>소속 중인 직원 수</span>
              <span>추가요금</span>
            </div>
            <div className="mt-02 flex items-baseline justify-between">
              <p className="text-display-sm font-bold">{sliderCount}명</p>
              <p className="text-display-sm font-bold text-group-onPrimary">
                {sliderSurcharge > 0 ? `+₩${formatWon(sliderSurcharge)}` : '없음'}
              </p>
            </div>

            {/* ui-core에 Slider 컴포넌트가 없어(추후작업_TODO.md 참고) 네이티브 range input을 그대로
                쓰되, 색상은 groupTheme 대비 토큰(--gt-on-primary, 흰색 계열)으로 맞춤(임의 색상
                아님). accent-color만으로는 브라우저마다 트랙의 "채워지지 않은" 구간이 기본 회색/
                검정 홈으로 남는 경우가 있어, 트랙 전체를 명시적으로 흰색 계열로 칠하는 로컬 CSS를
                같이 둔다(ui-core에 Slider가 없어 컴포넌트 자체를 손볼 수 없는 상황의 대안). */}
            <style>{`
              .staff-fee-slider {
                -webkit-appearance: none;
                appearance: none;
                width: 100%;
                height: 4px;
                border-radius: 9999px;
                background: var(--gt-on-primary);
                opacity: 0.9;
              }
              .staff-fee-slider::-webkit-slider-runnable-track {
                height: 4px;
                border-radius: 9999px;
                background: var(--gt-on-primary);
              }
              .staff-fee-slider::-moz-range-track {
                height: 4px;
                border-radius: 9999px;
                background: var(--gt-on-primary);
              }
              .staff-fee-slider::-webkit-slider-thumb {
                -webkit-appearance: none;
                appearance: none;
                width: 16px;
                height: 16px;
                margin-top: -6px;
                border-radius: 9999px;
                background: var(--gt-on-primary);
                cursor: pointer;
              }
              .staff-fee-slider::-moz-range-thumb {
                width: 16px;
                height: 16px;
                border: none;
                border-radius: 9999px;
                background: var(--gt-on-primary);
                cursor: pointer;
              }
            `}</style>
            <input
              type="range"
              min={0}
              max={SLIDER_MAX}
              step={1}
              value={sliderCount}
              onChange={(e) => {
                setSliderTouched(true);
                setSliderCount(Number(e.target.value));
              }}
              className="staff-fee-slider mt-05"
              aria-label="직원 수 시뮬레이션"
            />
            <div className="mt-01 flex items-center justify-between text-caption opacity-80">
              <span>0명</span>
              <span>{SLIDER_MAX}명↑</span>
            </div>

            <p className="mt-05 text-body-sm">
              {activeTiers.length === 0 ? (
                policyEffectiveFrom ? (
                  <>
                    {formatDate(policyEffectiveFrom)}부터 적용되는 요금이라 <strong className="font-bold">아직은 추가요금이 없어요</strong>
                  </>
                ) : (
                  '아직 적용되는 추가요금 정책이 없어요'
                )
              ) : (
                <>
                  지금{' '}
                  <strong className="font-bold text-group-onPrimary">
                    {currentTier
                      ? tierRangeLabel(currentTier, activeTiers.indexOf(currentTier) === activeTiers.length - 1)
                      : `0~${(activeTiers[0]?.minCnt ?? 1) - 1}명`}
                  </strong>{' '}
                  구간이라{' '}
                  {sliderSurcharge > 0 ? (
                    <>
                      <strong className="font-bold text-group-onPrimary">+{formatWon(sliderSurcharge)}원</strong>이 붙고 있어요
                    </>
                  ) : (
                    '추가요금이 없어요'
                  )}
                </>
              )}
            </p>
          </>
        )}
      </div>

      {/* 예상 청구 금액(C-5-4) + 인원수 구간표(C-5-5) — 가로 배열(사용자 요청, 2026-08-31) */}
      <div className="mt-06 grid grid-cols-1 gap-md md:grid-cols-2">
        {/* 예상 청구 금액 (C-5-4) — 현재 실제 구독 중인 티어 기준(요금제 비교 버튼 제거) */}
        <Card className="min-h-13">
          <h3 className="text-title-sm text-text">
            예상 청구 금액{sub && ` (${TIER_COPY[sub.tierCode].displayName} 요금제 기준)`}
          </h3>

          {subscriptionQuery.isLoading ? (
            <Skeleton className="mt-04" height={100} />
          ) : (
            <>
              <div className="mt-04 flex items-center justify-between text-body-sm">
                <span className="text-text-muted">기본구독료</span>
                <span className="text-text">₩{formatWon(baseMonthly)}/월</span>
              </div>
              <div className="mt-02 flex items-center justify-between text-body-sm">
                <span className="text-text-muted">직원 {realStaffCount}명 추가요금</span>
                <span className="text-text">
                  {sub?.tierCode === 'FREE'
                    ? '해당없음'
                    : applicableSurcharge > 0
                      ? `+₩${formatWon(applicableSurcharge)}/월`
                      : '없음'}
                </span>
              </div>
              <div className="mt-03 border-t border-border" />
              <div className="mt-03 flex items-center justify-between">
                <span className="text-body-sm text-text-muted">다음 결제 예정 금액</span>
                <span className="text-title-md font-bold text-group">₩{formatWon(estimatedTotal)}/월</span>
              </div>
            </>
          )}
        </Card>

        {/* 인원수 구간표 (C-5-5) — 시행 예정이라도 요금표 자체는 항상 보여주고, 우측에 실시일 안내 */}
        <Card className="min-h-13">
          <div className="flex items-center justify-between">
            <h3 className="text-title-sm text-text">인원수 구간별 추가요금</h3>
            {policyEffectiveFrom && (
              <span className="text-caption text-text-muted">정책 실시일 {formatDate(policyEffectiveFrom)}</span>
            )}
          </div>

          {tiersQuery.isLoading ? (
            <Skeleton className="mt-04" height={80} />
          ) : displayTiers.length === 0 ? (
            <EmptyState className="mt-04" title="요금 정책 정보가 없어요" />
          ) : (
            <table className="mt-04 w-full border-collapse text-body-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="pb-02 text-left font-normal text-text-muted">소속 중인 직원 수</th>
                  <th className="pb-02 text-right font-normal text-text-muted">월 추가요금</th>
                </tr>
              </thead>
              <tbody>
                {displayTiers.map((row) => (
                  <tr key={row.label} className="border-b border-border last:border-0">
                    <td className="py-03 text-text">{row.label}</td>
                    <td className="py-03 text-right text-text">
                      {row.amount > 0 ? `+₩${formatWon(row.amount)}` : '없음(기본 구독료만 청구)'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <p className="mt-03 text-caption text-text-muted">모든 유료 요금제(Starter·Pro·All-in)에 동일하게 적용돼요.</p>
        </Card>
      </div>

      {/* 직원 등록·소속해제 (C-5-6) — shell-web의 실제 직원 관리 화면(마스터 전용)으로 이동.
          설명 문구는 "결제일 스냅샷" 정책 안내(사용자와 논의, 2026-08-31) — 허점은
          추후작업_TODO.md에 별도 기록. */}
      <Card className="mt-06">
        <div className="flex flex-col items-start gap-sm sm:flex-row sm:items-center sm:justify-between">
          <h3 className="text-title-sm text-text">직원 등록·소속해제</h3>
          <Button variant="ghost" size="sm" onClick={() => navigate('/settings/employees')}>
            직원관리로 이동 →
          </Button>
        </div>
        <p className="mt-02 text-body-sm text-text-muted">
          직원 초대·소속해제는 직원관리 화면에서 진행해요. 이번 결제 주기 요금은 지금 바꿔도 변하지
          않고, 인원수는 다음 결제일 기준으로 다시 계산돼서 반영돼요.
        </p>
      </Card>
    </Page>
  );
}
